import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Search, 
  Package, 
  ShoppingCart, 
  Plus, 
  Minus,
  Loader2,
  RefreshCw,
  Star
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const CustomerCatalog = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [cart, setCart] = useState([]);

  useEffect(() => {
    loadCatalog();
    // Load cart from localStorage
    const savedCart = JSON.parse(localStorage.getItem('nas_customer_cart') || '[]');
    setCart(savedCart);
  }, []);

  const loadCatalog = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('nas_customer_token');
      if (!token) {
        navigate('/nas-portal/login');
        return;
      }
      
      const response = await axios.get(`${API}/api/nas-customer/catalog?token=${token}`);
      setProducts(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/nas-portal/login');
      } else {
        toast.error('Failed to load products');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getCartQuantity = (productId) => {
    const item = cart.find(c => c.product_id === productId);
    return item ? item.quantity : 0;
  };

  const updateCart = (product, quantity) => {
    let newCart = [...cart];
    const existingIndex = newCart.findIndex(c => c.product_id === product.product_id);
    
    if (quantity <= 0) {
      // Remove from cart
      newCart = newCart.filter(c => c.product_id !== product.product_id);
    } else if (existingIndex >= 0) {
      // Update quantity
      newCart[existingIndex].quantity = quantity;
    } else {
      // Add to cart
      newCart.push({
        product_id: product.product_id,
        name: product.name,
        price: product.price,
        unit: product.unit,
        quantity: quantity,
        available_quantity: product.available_quantity
      });
    }
    
    setCart(newCart);
    localStorage.setItem('nas_customer_cart', JSON.stringify(newCart));
    
    // Dispatch event for cart count update
    window.dispatchEvent(new Event('nas_cart_updated'));
  };

  const addToCart = (product) => {
    const currentQty = getCartQuantity(product.product_id);
    if (currentQty >= product.available_quantity) {
      toast.error(`Maximum available: ${product.available_quantity}`);
      return;
    }
    updateCart(product, currentQty + 1);
    toast.success(`Added ${product.name} to cart`);
  };

  const filteredProducts = products.filter(p => {
    const term = searchTerm.toLowerCase();
    return (
      p.name?.toLowerCase().includes(term) ||
      p.category?.toLowerCase().includes(term) ||
      p.supplier?.toLowerCase().includes(term) ||
      p.brand?.toLowerCase().includes(term) ||
      p.description?.toLowerCase().includes(term)
    );
  }
  );

  return (
    <div className="p-4 pb-20 lg:pb-4 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Product Catalog</h1>
          <p className="text-sm text-slate-500">{products.length} products available</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="icon" 
            onClick={loadCatalog}
            disabled={isLoading}
          >
            <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
          </Button>
          <Button 
            className="bg-green-600 hover:bg-green-700"
            onClick={() => navigate('/nas-portal/cart')}
            data-testid="view-cart-btn"
          >
            <ShoppingCart size={18} className="mr-2" />
            Cart ({cart.length})
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
        <Input
          placeholder="Search by name, brand, or supplier..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="product-search-input"
        />
      </div>

      {/* Products Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-green-600" />
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="text-center py-20">
          <Package size={48} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">No products found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProducts.map(product => {
            const cartQty = getCartQuantity(product.product_id);
            const isInCart = cartQty > 0;
            
            return (
              <Card 
                key={product.product_id} 
                className={`bg-white overflow-hidden ${isInCart ? 'ring-2 ring-green-500' : ''}`}
                data-testid={`product-card-${product.product_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-semibold text-slate-800">{product.name}</h3>
                      {/* Brand/Category and Supplier info */}
                      {(product.category || product.supplier) && (
                        <p className="text-xs text-slate-500">
                          {product.category && <span className="font-medium">{product.category}</span>}
                          {product.category && product.supplier && ' • '}
                          {product.supplier && <span>{product.supplier}</span>}
                        </p>
                      )}
                      {product.description && (
                        <p className="text-sm text-slate-400 line-clamp-1 mt-1">{product.description}</p>
                      )}
                    </div>
                    {product.has_custom_price && (
                      <Badge variant="outline" className="text-amber-600 border-amber-300">
                        <Star size={12} className="mr-1" />
                        Special
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-lg font-bold text-green-600">
                        ₱{product.price?.toLocaleString()}
                        <span className="text-sm text-slate-500 font-normal">/{product.unit}</span>
                      </p>
                      {product.has_custom_price && product.default_price !== product.price && (
                        <p className="text-sm text-slate-400 line-through">
                          ₱{product.default_price?.toLocaleString()}
                        </p>
                      )}
                    </div>
                    <Badge variant={product.available_quantity > 10 ? "secondary" : "destructive"}>
                      {product.available_quantity} in stock
                    </Badge>
                  </div>
                  
                  {/* Location badges */}
                  <div className="flex flex-wrap gap-1 mb-3">
                    {product.locations?.map((loc, idx) => (
                      <Badge 
                        key={idx} 
                        variant="outline" 
                        className={loc.name === 'Bodega' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-purple-50 text-purple-700 border-purple-200'}
                      >
                        {loc.name}: {loc.stock}
                      </Badge>
                    ))}
                  </div>
                  
                  {/* Cart Controls */}
                  {isInCart ? (
                    <div className="flex items-center justify-between bg-green-50 rounded-lg p-2">
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-8 w-8"
                        onClick={() => updateCart(product, cartQty - 1)}
                        data-testid={`decrease-qty-${product.product_id}`}
                      >
                        <Minus size={16} />
                      </Button>
                      <span className="font-semibold text-green-700">
                        {cartQty} {product.unit}
                      </span>
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-8 w-8"
                        onClick={() => {
                          if (cartQty >= product.available_quantity) {
                            toast.error(`Maximum available: ${product.available_quantity}`);
                            return;
                          }
                          updateCart(product, cartQty + 1);
                        }}
                        disabled={cartQty >= product.available_quantity}
                        data-testid={`increase-qty-${product.product_id}`}
                      >
                        <Plus size={16} />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={() => addToCart(product)}
                      disabled={product.available_quantity <= 0}
                      data-testid={`add-to-cart-${product.product_id}`}
                    >
                      <Plus size={18} className="mr-2" />
                      Add to Cart
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CustomerCatalog;
